import axios from 'axios'
import { store } from '../store/store.js'

axios.defaults.baseURL = 'https://portal.ssparisi.com/rest-api'
axios.defaults.headers.post['Content-Type'] = 'application/json'

const SENSOR = process.env.SENSOR_API ? process.env.SENSOR_API : 'https://sensor-api.ssparisi.com'

axios.interceptors.request.use(
  config => {
    config.headers['Selected-Company'] = store.state.company ? store.state.company.public_key : ''
    config.headers['Authorization'] = store.state.user.token
    return config
  },
  error => Promise.reject(error)
)

axios.interceptors.response.use(response => response, (error) => {
  if (store.state.user.token && error.response.data.statusCode === 401) {
    localStorage.removeItem('token')
    localStorage.removeItem('vuex')
    window.dispatchEvent(new Event('session_expired'))
  }
  return Promise.reject(error)
})

export const httpService = {

  /* ------------------------------- ACCESS --------------------------------- */
  login (dt) {
    return axios.post('/login', dt)
  },

  forgotPassword (dt) {
    return axios.put(`/users/${dt.email}/password/reset`, dt)
  },

  resetPassword (dt) {
    return axios.put(`/users/password/confirmation-reset`, dt)
  },

  changePassword (dt) {
    return axios.put('/users/authenticated/change-password', dt)
  },

  completeRegistry (dt) {
    return axios.put('/users/account/confirmation', dt)
  },

  /* -------------------------------- USER ---------------------------------- */

  getUserList (query) {
    return axios.get(`/users${this.createQueryString(query)}`)
  },

  saveNewUser (dt) {
    return axios.post('/users', dt).then(res => {
      this.saveUserPermissions(store.state.company.public_key, 'd300660e4320d8d5c8ecb48035b67815', res.data[0].public_key, [
        '2555f239ad7c1717f8ebda2daf7fe07b',
        '11d0632099c633b1dd73b9bac676a621',
        '72cc27f26644651e5dbf72c4f9a22f35',
        'f901d8efe84b23cb63ff741ba3b76943',
        '729fc37cb039db91d8dd6f6960cd8c0d',
        'da9471023208e66e1bca4cedbc662032',
        '80a7c7bb342012d2f4f46148049cb972',
        '8a2fd41ee4b3be69484fb1114cc95341',
        'fee433134b6bab184b3f25e87da62aa4',
        'c20b1cb6d84a5f133695c5a1efd3379a',
        '3ff404bca7f1307a56b8976209119d4d',
        '3e3fc7d1f943ad6bfe6c6b056873ce8e',
        '2e39d305f43aa421df0c1c6ff28afa66',
        '9c4ed0d746c5a7913cab38a938780acf',
        '5bc919ca057f99025eabfda5818b9741',
        '1f152b722d64ba573cd756fe5757ec80',
        'b0151a967f25aac481c7ea315366e9f1',
        '2f73831d056ff52bb0028d0a50f791e9',
        '545697e894825c828956e7ce887791a4',
        '0053d6310971f7162b1fbcba81647685',
        'a6a5dd0f28c2447842c5b000f9a54cbf'
      ])
      return res
    })
  },

  sendUserEnvite (email) {
    return axios.put(`/users/${email}/account/send-confirmation`)
  },

  editUser (dt) {
    return axios.put(`/users/${dt.public_key}`, dt)
  },

  deleteUser (user) {
    return axios.delete(`/users/${user.public_key}`)
  },

  saveUserRoles (dt, userId) {
    return axios.put(`/users/${userId}/roles`, dt)
  },

  getUserPermissions (companyId, productId, userId) {
    return axios.get(`/users/${userId}/companies/${companyId}/products/${productId}/functionalities`)
  },

  saveUserPermissions (companyId, productId, userId, dt) {
    return axios.put(`/users/${userId}/companies/${companyId}/products/${productId}/functionalities`, dt)
  },

  removeUserAccess (companyId, productId, userId) {
    return axios.delete(`/companies/${companyId}/products/${productId}/users/${userId}`)
  },

  /* ------------------------------- COMPANY -------------------------------- */

  /* Obtém as empresas master (clientes diretos da SS Parisi) */
  getUserCompanies (query) {
    return axios.get(`/companies/${this.createQueryString(query)}&status=${query.status || '1'}&tag=${query.tags ? query.tags.join(',') : ''}`)
  },

  /**
   * Obtém as empresas clientes (clientes dos clientes da SS Parisi). Se a SS Parisi for a empresa atual,
   * retorna as empresas master (clientes diretos da SS Parisi).
   */
  getCompanyCustomers (query) {
    return axios.get(`/companies/customers${this.createQueryString(query)}&status=${query.status || '1'}&tag=${query.tags ? query.tags.join(',') : ''}`)
  },

  getCompanyById (id) {
    return axios.get(`/companies/${id}`)
  },

  saveNewCompany (dt) {
    return axios.post('/companies', dt)
  },

  editCompany (dt) {
    return axios.put(`/companies/${dt.id}`, dt)
  },

  deleteCompany (id) {
    return axios.delete(`/companies/${id}`)
  },

  getCompanyUsers (id, query) {
    return axios.get(`/companies/${id}/users${this.createQueryString(query)}`)
  },

  removeUserFromCompany(comp_pk, user_pk) {
    return axios.delete(`/companies/${comp_pk}/user/${user_pk}`)
  },

  getCompanySensors (id) {
    return axios.get(`${SENSOR}/sensor/connected/${id}`)
  },

  getCompanyProducts (id) {
    return axios.get(`/companies/${id}/products`)
  },

  setCompanyProduct (prd, id) {
    return axios.post(`/companies/${id}/products/${prd}`, {status: 1})
  },

  removeCompanyProduct (prd) {
    return axios.delete(`/companies/products/${prd}`)
  },

  getCompaniesByProduct (productId, query) {
    return axios.get(`products/${productId}/companies${this.createQueryString(query)}`)
  },

  /**
   * Retorna os clientes (clientes dos clientes da SS Parisi) que possuem determinado produto.
   * Se a empresa atual for a SS Parisi, retorna os clientes diretos da SS Parisi (empresas master).
   */
  getCustomersByProduct (productId, query) {
    return axios.get(`companies/products/${productId}/customers${this.createQueryString(query)}`)
  },

  /* ------------------------------- PRODUCTS --------------------------------- */

  changeProductStatus (prdId, st) {
    return axios.put(`companies/products/${prdId}`, {status: st})
  },

  getProductsList () {
    return axios.get('/products?page=0&limit=50')
  },

  getProductPermissions (productId) {
    return axios.get(`/products/${productId}/functionalities`)
  },

  /* ------------------------------- OTHERS --------------------------------- */

  searchCNAE (search) {
    return axios.get('/cnaes?search=' + search)
  },

  consultCNPJ (cnpj, timeout) {
    return axios.get(`/cnpj/${cnpj}`, { timeout: timeout })
  },

  consultCEP (cep) {
    return axios.get(`/cep/${cep}`)
  },

  getCompanyTagList () {
    return axios.get('/companies/selected/tags')
  },

  createQueryString (query) {
    let search = ''
    if (query.filter.field && query.filter.value && query.filter.value !== '') {
      search = `field=${query.filter.field}&value=${query.filter.value}&`
    }
    return `?${search}page=${query.page}&limit=${query.limit}`
  }

}



// WEBPACK FOOTER //
// ./src/providers/http-service.js